/**
 * Every user-facing string that crosses a boundary is named here. No inline copy in a
 * component or a route handler (packages/returns-shared/AGENTS.md).
 *
 * Coverage requirement: every ineligibility reason (docs/05), every policy/API failure
 * (docs/06), every line state shown on the tracking page (docs/04), and every canonical
 * return reason (fixtures/merchants.json).
 */
export const MESSAGE_KEYS = [
  // --- Ineligibility reasons (docs/05 LineEligibility.reasonCode) ---
  "eligibility.window_closed",
  "eligibility.sku_excluded",
  "eligibility.already_returned",
  "eligibility.not_delivered",
  "eligibility.eligible",

  // --- Policy / API failures (docs/06) ---
  "error.order_not_found",
  "error.customer_blocked",
  "error.returns_disabled",
  "error.policy_violation",
  "error.rate_limited",
  "error.internal",
  "error.invalid_code",
  "error.cancel_not_allowed",

  // --- Policy violations raised on submit (docs/05 evaluation order) ---
  "violation.returns_disabled",
  "violation.order_not_eligible",
  "violation.window_closed",
  "violation.sku_excluded",
  "violation.customer_blocked",
  "violation.reason_not_offered",
  "violation.photo_required",
  "violation.note_required",
  "violation.resolution_not_offered",
  "violation.no_resolution_available",
  "violation.dropoff_not_allowed",
  "violation.qty_exceeds_ordered",
  "violation.line_not_found",
  "violation.no_lines_selected",
  "violation.cancel_not_allowed",

  // --- Return reasons offered to the shopper ---
  "reason.size",
  "reason.damaged",
  "reason.notlike",
  "reason.changed",
  "reason.wrong",
  "reason.spoiled",
  "reason.other",

  // --- System-originated reasons. NEVER offered in the shopper's picker: nobody chose
  //     these, they happened to the parcel (docs/04 "Entry points").
  "reason.cod_refused",
  "reason.delivery_failed",

  // --- Resolution types ---
  "resolution.refund",
  "resolution.exchange",
  "resolution.credit",

  // --- Collection methods ---
  "collection.pickup",
  "collection.dropoff",

  // --- Line states, as shown on the shopper tracking page (docs/04) ---
  "state.requested",
  "state.approved",
  "state.awaiting_pickup",
  "state.in_transit",
  "state.received",
  "state.inspected",
  "state.booking_failed",
  "state.cancelled",
  "state.restocked",
  "state.quarantined",
  "state.disposed",
  "state.rejected",
  "state.lost_in_transit",
  "state.expired",
  "state.completed",

  // --- Merchant-facing state groups (schemas/state-group.ts). Nine plain words for the
  //     Returns tab: filter chips and the label on every row. Not the shopper's vocabulary.
  "state_group.waiting_for_you",
  "state_group.waiting_for_pickup",
  "state_group.with_courier",
  "state_group.at_warehouse",
  "state_group.inspected",
  "state_group.refund_due",
  "state_group.closed",
  "state_group.rejected",
  "state_group.cancelled",
  // --- Who did a thing, on the timeline (return_event.actor_type) ---
  "actor.customer",
  "actor.merchant",
  "actor.warehouse",
  "actor.system",
  "actor.carrier",

  // --- Timeline events that are not a state step ---
  "event.created",
  "event.unknown",

  // --- Grades (exposed only once inspected, docs/06) ---
  "grade.sellable",
  "grade.damaged",
  "grade.missing",

  // --- Rejections that carry a named policy failure to the shopper ---
  "rejection.merchant_declined",
  "rejection.policy_refused",
  "rejection.item_not_as_declared",
  "rejection.outside_window",

  // --- Fees ---
  "fee.none",
  "fee.flat",
  "fee.percent",

  // --- Portal states ---
  "portal.closed",
  "portal.no_resolutions_enabled",
  "portal.pickup_only",
  "portal.photo_required_hint",
  "portal.resolution_is_a_request",

  // --- Screen titles and standfirsts, one per step of docs/08 ---
  "screen.lookup.title",
  "screen.lookup.description",
  "screen.lookup.phone_description",
  "screen.lookup.code_title",
  "screen.lookup.code_description",
  "screen.lookup.orders_title",
  "screen.lookup.orders_description",
  "screen.items.title",
  "screen.items.description",
  "screen.reason.title",
  "screen.reason.description",
  "screen.evidence.title",
  "screen.evidence.description",
  "screen.resolution.title",
  "screen.resolution.description",
  "screen.review.title",
  "screen.review.description",
  "screen.confirmation.title",
  "screen.track.title",
  "screen.track.description",

  // --- Actions ---
  "action.continue",
  "action.back",
  "action.submit",
  "action.retry",
  "action.track",
  "action.find_order",
  "action.send_code",
  "action.verify_code",
  "action.resend_code",
  "action.change_phone",
  "action.have_order_ref",
  "action.use_phone_instead",
  "action.skip",
  "action.add_photo",
  "action.remove_photo",
  "action.start_over",
  "action.change",
  "action.cancel_return",
  "action.cancel_confirm",
  "action.keep_return",
  "action.refresh_status",

  // --- Generic UI chrome ---
  "ui.loading",
  "ui.offline",
  "ui.error_generic",
  "ui.empty_generic",
  "ui.step_of",
  "ui.step_label.lookup",
  "ui.step_label.items",
  "ui.step_label.reason",
  "ui.step_label.evidence",
  "ui.step_label.resolution",
  "ui.step_label.review",
  "ui.language_switch",
  "ui.optional",
  "ui.route_not_found",
  "ui.server_confirms",

  // --- Form fields ---
  "field.order_ref.label",
  "field.order_ref.hint",
  "field.order_ref.placeholder",
  "field.phone.label",
  "field.phone.hint",
  "field.phone.placeholder",
  "field.code.label",
  "field.code.hint",
  "validation.code_required",
  "shopper.orders.empty",
  "shopper.orders.items",
  "shopper.orders.placed",
  "field.reference.label",
  "field.reference.placeholder",
  "field.sub_reason.label",
  "field.note.label",
  "field.note.hint",
  "field.other_note.label",
  "field.other_note.hint",
  "field.quantity.label",

  // --- Client-side validation. Never a policy decision — the server owns those. ---
  "validation.order_ref_required",
  "validation.phone_required",
  "validation.reference_required",
  "validation.reason_required",
  "validation.resolution_required",
  "validation.collection_required",
  "validation.photo_required",
  "validation.other_note_required",
  "validation.select_at_least_one",

  // --- Step 1, lookup ---

  // --- Step 2, item selection ---
  "items.select_hint",
  "items.none_eligible",
  "items.empty",

  // --- Step 3, reason ---
  "reason.select_hint",
  "reason.none_offered",

  // --- Step 4, evidence ---
  "evidence.required_note",
  "evidence.optional_note",
  "evidence.photos_label",
  "evidence.uploading",
  "evidence.upload_failed",
  "evidence.no_photos",

  // --- Step 5, resolution ---
  "resolution.select_hint",
  "resolution.exchange_unavailable",

  // --- Fee disclosure (policy check 9: shown here, and again at review) ---
  "fee.disclosure_title",
  "fee.deducted_note",
  "fee.total_label",

  // --- Step 6, review ---
  "review.items_heading",
  "review.reason_heading",
  "review.resolution_heading",
  "review.collection_heading",
  "review.collection_hint",
  "review.evidence_heading",
  "review.photos_attached",
  "review.fee_heading",

  // --- Step 7, confirmation ---
  "confirmation.heading",
  "confirmation.reference_label",
  "confirmation.what_next",

  // --- Step 8, tracking ---
  "track.intro",
  "track.reference_label",
  "track.copy_reference",
  "track.copy_link",
  "track.copied",
  "track.keep_hint",
  "track.lines_heading",
  "track.timeline_heading",
  "track.received_note",
  "track.rejection_heading",
  "track.approved_headline",
  "track.grade_label",
  "track.qty_expected",
  "track.qty_received",
  "track.empty_timeline",

  // --- Step 8, cancelling. A cancellation is a withdrawal, never a refusal: none of this
  //     copy may read as blame, and none of it promises money moving (ADR-003). Whether
  //     the action is offered at all is decided by `isCancellableState` in @sirdab/returns-core.
  "cancel.confirm_title",
  "cancel.confirm_body",
  "cancel.scope_note",
  "cancel.done",
  "cancel.unavailable_note",
  "cancel.failed_note",
] as const;

export type MessageKey = (typeof MESSAGE_KEYS)[number];
